<template>
    <div>
        <div class="pb-10 mb-12 border-b border-gray-200">
            <h2 class="pb-1 text-2xl font-bold text-left text-black no-underline normal-case non-italic">Invest</h2>
            <p class="text-lg font-normal text-left no-underline normal-case non-italic">Please indicate the amount that you would like to invest.</p>
        </div>

        <app-errors :form="form" class="mb-10"></app-errors>

        <div class="max-w-md mx-auto">
            <div class="mb-8">
                <label class="block text-sm font-medium text-gray-700">Total Amount</label>
                <div class="relative mt-1">
                    <input type="text" v-model.lazy="form.amount" v-money="moneyArgs" @keyup="onAmountInput($event)" pattern="[0-9]*" inputmode="numeric" class="px-0 border-0 border-b border-gray-200 rounded-none focus:border-teal focus:shadow-none focus:ring-0">
                    <span v-show="amountError" class="block absolute -bottom-4.5 left-0 text-xs text-red-600">{{ amountError }}</span>
                </div>
            </div>

            <div class="relative mb-8">
                <div class="absolute ml-4 top-1 left-28 group">
                    <svg class="w-4 h-4 text-gray-300 hover:text-gray-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"></path></svg>
                    <div class="absolute hidden p-6 mb-1 -ml-4 text-sm font-semibold text-white bg-black rounded-md group-hover:block bottom-full left-full w-96">
                        This is the amount you will invest today. The minimum initial capital call to invest today is <span v-if="series == 'nsight'">$25,000</span><span v-else>$50,000</span>. The maximum investment amount today is $250,000. If you invest more than $250,000, we will make a capital call later for the funds above the $250,000 investment you make today.
                    </div>
                </div>
                <label class="block text-sm font-medium text-gray-700">Capital Call Today</label>
                <div class="relative mt-1">
                    <input type="text" v-model.lazy="form.capital_call_today" v-money="moneyArgs" readonly="true" class="px-0 border-0 rounded-none focus:shadow-none focus:ring-0">
                </div>
            </div>

            <div v-if="showFeeMessage && series != 'nsight'" class="mb-8">
                <div class="p-4 border border-gray-200 rounded-md">
                    <label class="block text-sm font-medium text-gray-700">True Up Dollar Amount</label>

                    <div class="p-4 my-3 space-y-4 text-sm text-red-700 bg-red-100 rounded-md">
                        <!-- <p class="font-medium text-red-800">The following is not a management fee. This is a one time cost of your true-up applied to existing investors for those investors coming into the fund after portfolio company investments have already been made.</p>
                        <p>If you invest prior to March 31, 2022 you will have 3% applied toward interest.</p>
                        <p>If you invest after March 31, 2022 you will have 5% applied toward interest.</p> -->
                        <p class="font-medium text-red-800">The following is not a management fee. This is a one-time cost of your true-up applied to existing investors for those investors coming into the fund after portfolio company investments have already been made. As of 7/1/2022, you will have 5% applied toward interest.</p>
                    </div>

                    <div class="relative">
                        <input type="text" v-model="form.true_up_dollar" readonly="true" class="px-0 border-0 rounded-none focus:shadow-none focus:ring-0">
                    </div>
                </div>
            </div>

            <div class="mt-14">
                <h3 class="mb-6 text-base font-medium text-center">Fund Your Investment</h3>

                <div class="mt-10">
                    <h4 class="mb-4 text-sm font-medium leading-5 text-left no-underline normal-case non-italic">How do you want to fund your investment now?</h4>
                    <div v-if="investingType == 'ira'">
                        <radio-field v-model="form.fund.which" value="ira">IRA Account</radio-field>
                    </div>
                    <!-- <div v-else-if="isCapitalCallHundredK" class="flex items-center space-x-8">
                        <radio-field v-model="form.fund.which" :disabled="investingType == 'ira'" value="wire">Wire</radio-field>

                        <template v-if="series != 'nsight'">
                            <radio-field v-model="form.fund.which" value="ira">IRA</radio-field>
                        </template>
                    </div> -->
                    <div v-else class="space-y-4">
                        <radio-field v-model="form.fund.which" :disabled="investingType == 'ira'" value="wire">Wire</radio-field>

                        <div class="flex items-center space-x-8">
                            <radio-field v-model="form.fund.which" :disabled="investingType == 'ira'" value="ach">ACH</radio-field>

                            <template v-if="series != 'nsight'">
                                <radio-field v-model="form.fund.which" value="ira">IRA</radio-field>
                            </template>
                        </div>
                    </div>
                </div>

                <div v-if="form.fund.which == 'ach'" class="mt-10 space-y-6">
                    <h3 class="text-base font-normal text-center">ACH Transfer to Fund Your Mammoth Account</h3>

                    <app-button @click="handlePlaid($event, 'fund')" :busy="isBusy" class="!w-full" :class="{
                        '!bg-teal pointer-events-none': form.fund.plaid,
                        '!text-white !bg-[#0870CF]': !form.fund.plaid
                    }">
                        <span v-if="form.fund.plaid" class="flex items-center">
                            <svg class="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path></svg>
                            <span>Account Connected</span>
                        </span>

                        <span v-else>Connect Account</span>
                    </app-button>

                    <div v-show="form.fund.plaid" class="space-y-5">
                        <text-field v-model="form.fund.accountName" label="Account Holder Name" :required="true"></text-field>

                        <text-field v-model="form.fund.nickname" label="Account Nickname" :required="true"></text-field>

                        <select-field v-model="form.fund.accountType" label="Account Type" :options="accountTypes"></select-field>

                        <div class="relative">
                            <div v-show="form.fund.accountType == 'Checking'" class="absolute -top-0.5 right-0"><a href="#" @click.prevent="showCheckReference = true" class="text-base text-gray-400 no-underline hover:text-gray-600">see reference here</a></div>
                            <text-field v-model="form.fund.accountNumber" pattern="[0-9]*" inputmode="numeric" v-maska="'#*'" label="Account Number" :required="true"></text-field>
                        </div>

                        <text-field v-model="form.fund.routingNumber" pattern="[0-9]*" inputmode="numeric" v-maska="'#########'" label="Routing Number" :required="true"></text-field>
                    </div>

                    <template v-if="!form.waive_fee">
                        <div v-if="form.fund.which != 'ach' || (form.fund.which == 'ach' && form.fund.plaid)" class="pt-5">
                            <h4 class="mb-1 text-sm font-medium leading-5 text-left no-underline normal-case non-italic">Use this account to pay for my annual <span v-if="series == 'nsight'">$500</span><span v-else>$399</span> administration fee?</h4>
                            <p class="text-sm font-light text-left no-underline normal-case non-italic">Note: You will be notified prior to this fee being pulled and can change it then, if you prefer.</p>

                            <div class="flex mt-4">
                                <div class="mr-8">
                                    <radio-field v-model="form.fee.which" value="yes">Yes</radio-field>
                                </div>

                                <radio-field v-model="form.fee.which" value="no">No</radio-field>
                            </div>
                        </div>
                    </template>
                </div>

                <div v-if="form.fund.which == 'wire'" class="mt-10 -mx-0 sm:-mx-20">
                    <h3 class="mb-8 text-lg font-normal text-center no-underline normal-case non-italic">
                        Wire Transfer Instructions
                    </h3>

                    <div class="grid grid-cols-1 gap-6 text-sm font-medium text-gray-600 sm:grid-cols-2">
                        <div class="col-span-2 sm:col-span-1">
                            <h3 class="block mb-2 text-gray-400">Wire To</h3>
                            <div>TRISTATE CAPITAL BANK</div>
                            <div>ONE OXFORD CENTRE, SUITE 2700</div>
                            <div>301 GRANT STREET</div>
                            <div>PITTSBURGH, PA 15219</div>
                        </div>

                        <div class="col-span-2 sm:col-span-1">
                            <h3 class="block mb-2 text-gray-400">Custodian / Account Name</h3>
                            <div>TRISTATE CAPITAL BANK</div>
                            <div>ONE OXFORD CENTRE, SUITE 2700</div>
                            <div>301 GRANT STREET</div>
                            <div>PITTSBURGH, PA 15219</div>
                        </div>

                        <div class="col-span-2 sm:col-span-1">
                            <h3 class="block mb-2 text-gray-400">FFCT:</h3>
                            <div>OFFERING NAME AND INVESTOR NAME</div>
                        </div>

                        <div class="col-span-2 sm:col-span-1">
                            <h3 class="block mb-2 text-gray-400">ABA (Routing Number)</h3>
                            <div>043019 0 03</div>
                        </div>

                        <div class="col-span-2 sm:col-span-1">
                            <h3 class="block mb-2 text-gray-400">Swift ID</h3>
                            <div>TRTTUS33</div>
                        </div>

                        <div class="col-span-2 sm:col-span-1">
                            <h3 class="block mb-2 text-gray-400">Account Number</h3>
                            <div>0220003339</div>
                        </div>
                    </div>
                </div>
            </div><!-- FUND ACCOUNT -->

            <div v-if="showOtherAccount && !form.waive_fee" class="mt-14">
                <h3 class="mb-6 text-base font-medium text-center">Admin Fee Transfer Account</h3>

                <div v-if="form.fund.which != 'ach'" class="text-sm font-light text-left no-underline normal-case non-italic">
                    <p class="mb-3">This account is for your annual <span v-if="series == 'nsight'">$500</span><span v-else>$399</span> administration fee.</p>
                    <p>Note: You will be notified prior to this fee being pulled and can change it at that time, if you choose.</p>
                </div>

                <div class="mt-8 space-y-5">
                    <text-field v-model="form.fee.accountName" label="Account Holder Name" :disabled="isAccountDisabled('fee')" :required="true"></text-field>

                    <text-field v-model="form.fee.nickname" label="Account Nickname" :disabled="isAccountDisabled('fee')" :required="true"></text-field>

                    <select-field v-model="form.fee.accountType" label="Account Type" :options="accountTypesAlt" :disabled="isAccountDisabled('fee')"></select-field>

                    <div class="relative">
                        <div v-show="form.fee.accountType == 'Checking'" class="absolute -top-0.5 right-0"><a href="#" @click.prevent="showCheckReference = true" class="text-base text-gray-400 no-underline hover:text-gray-600">see reference here</a></div>
                        <text-field v-model="form.fee.accountNumber" pattern="[0-9]*" inputmode="numeric" v-maska="'#*'" label="Account Number" :disabled="isAccountDisabled('fee')" :required="true"></text-field>
                    </div>

                    <text-field v-model="form.fee.routingNumber" pattern="[0-9]*" inputmode="numeric" v-maska="'#########'" label="Routing Number" :disabled="isAccountDisabled('fee')" :required="true"></text-field>
                </div>

                <div v-if="form.fund.which != 'ira'" class="mt-8">
                    <h4 class="mb-1 text-sm font-medium leading-5 text-left no-underline normal-case non-italic">Use this account to for your distributions and credits?</h4>

                    <div class="flex mt-4">
                        <div class="mr-8">
                            <radio-field v-model="form.distribution.which" value="yes">Yes</radio-field>
                        </div>

                        <radio-field v-model="form.distribution.which" value="no">No</radio-field>
                    </div>
                </div>
            </div><!-- ADMIN FEE ACCOUNT -->

            <div v-if="showOtherAccount && form.fund.which != 'ira'" class="space-y-6 mt-14">
                <p class="text-[#721C24] text-sm font-light normal-case no-underline non-italic text-left">Please enter the bank account information for all distributions and credits you will receive.</p>

                <h3 class="text-base font-medium text-center">ACH Distribution Account</h3>

                <div class="space-y-5">
                    <text-field v-model="form.distribution.accountName" label="Account Holder Name" :disabled="isAccountDisabled('distribution')" :required="true"></text-field>

                    <text-field v-model="form.distribution.nickname" label="Account Nickname" :disabled="isAccountDisabled('distribution')" :required="true"></text-field>

                    <select-field v-model="form.distribution.accountType" label="Account Type" :options="accountTypes" :disabled="isAccountDisabled('distribution')"></select-field>

                    <div class="relative">
                        <div v-show="form.distribution.accountType == 'Checking'" class="absolute -top-0.5 right-0"><a href="#" @click.prevent="showCheckReference = true" class="text-base text-gray-400 no-underline hover:text-gray-600">see reference here</a></div>
                        <text-field v-model="form.distribution.accountNumber" pattern="[0-9]*" inputmode="numeric" v-maska="'#*'" label="Account Number" :disabled="isAccountDisabled('distribution')" :required="true"></text-field>
                    </div>

                    <text-field v-model="form.distribution.routingNumber" pattern="[0-9]*" inputmode="numeric" v-maska="'#########'" label="Routing Number" :disabled="isAccountDisabled('distribution')" :required="true"></text-field>
                </div>
            </div><!-- DISTRIBUTION ACCOUNT -->

            <div class="py-28 sm:flex sm:justify-end">
                <div class="inline-flex items-center space-x-3">
                    <app-button @click="handleBack" class="!border-gray-200 !bg-white !text-gray-400">Back</app-button>

                    <app-button @click="handleNext" :busy="form.busy">Save and Continue</app-button>
                </div>
            </div>

            <div>
                <h3 class="block mb-1 text-sm font-medium text-gray-700">Fund Transfer Notice:</h3>
                <div class="space-y-5 text-sm text-silver-gray">
                    <p>Investor funds are held in escrow and sent to Mammoth each Friday via ACH and Wire.</p>
                    <p>You will be fully invested once funds have cleared and will receive written notification from Mammoth.</p>
                    <p>It may take up to 10 days to clear your account.</p>
                </div>
            </div>
        </div>
    </div>

    <teleport to="body">
        <div v-show="northPlaid.show" class="fixed inset-0 overflow-y-auto" style="z-index: 999999;">
            <div class="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div class="fixed inset-0 transition-all transform pointer-events-none">
                    <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
                </div>

                <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <div class="inline-block px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform sm:my-8 sm:align-middle sm:w-full sm:p-6" style="max-width: 90%; max-height: 90%;">
                    <div class="rounded-md shadow">
                        <div class="relative p-3 bg-white" style="min-height: 100px;">
                            <app-spinner class="!w-10 !h-10 z-[99998]"></app-spinner>
                            <div v-if="northPlaid.src != ''" class="relative z-[99999] bg-white" :class="{
                                'opacity-0': northPlaid.src == ''
                            }"><iframe :src="northPlaid.src" style="width: 100%; height: 100%; min-height: 1020px;" frameborder="0"></iframe></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </teleport>

    <teleport to="body">
        <div v-show="showCheckReference" class="fixed inset-0 overflow-y-auto" style="z-index: 999999;">
            <div class="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div class="fixed inset-0 transition-all transform pointer-events-none">
                    <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
                </div>

                <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <div class="inline-block px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform sm:my-8 sm:align-middle sm:w-full sm:max-w-4xl sm:p-6">
                    <div class="relative bg-white rounded-md shadow">
                        <div class="absolute top-0 right-0 block p-2 bg-white">
                            <button type="button" @click="showCheckReference = false" class="text-gray-400 bg-white rounded-md hover:text-gray-500 focus:outline-none focus:ring-0">
                                <svg class="w-6 h-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>

                        <div class="p-3"><img :src="asset('img/check-reference.jpg')" class="w-full h-auto mx-auto" alt="checking-reference"></div>
                    </div>
                </div>
            </div>
        </div>
    </teleport>

    <teleport to="body">
        <div v-show="!isReloaded" class="fixed inset-0 overflow-y-auto z-[99999]">
            <div class="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div class="fixed inset-0 z-10 bg-gray-800 bg-opacity-25 backdrop-filter backdrop-blur-sm"></div><!-- overlay -->

                <span class="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span><!-- center trick -->

                <div class="relative z-20 inline-block overflow-hidden align-bottom bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                    <div class="text-center bg-white bg-left-top bg-no-repeat bg-cover p-14" :style="'background-image: url(' + asset('img/panel-bg.png') + ')'">
                        <h3 class="mb-8 text-2xl font-bold text-black">This may take a few moments. <br/>Please do not close your browser</h3>
                        <div class="mx-auto w-28 h-28">
                            <img :src="asset('img/spinner.gif')" alt="spinner" class="w-full h-auto">
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </teleport>
</template>

<script>
import AppButton from '@Components/Button'
import AppDivider from '@Components/Divider'
import AppErrors from '@Components/Errors'
import AppSpinner from '@Components/Spinner'
import TextField from '@Fields/Text'
import SelectField from '@Fields/Select'
import RadioField from '@Fields/Radio'
import CheckboxField from '@Fields/Checkbox'
import useForm from '@/form'
import { useStore } from 'vuex'
import { computed, onMounted, onUnmounted, reactive, ref, watch } from 'vue'
import { usePage } from '@inertiajs/inertia-vue3'
import { Inertia } from '@inertiajs/inertia'

export default {
    components: {
        AppButton,
        AppDivider,
        AppErrors,
        AppSpinner,
        TextField,
        SelectField,
        RadioField,
        CheckboxField
    },
    props: ['series'],
    emits: ['prev', 'next', 'process', 'complete', 'goto'],
    setup(props, { emit }){
        const store = useStore()

        const isBusy = ref(false)

        const isReloaded = ref(false)

        const partnerCodeLogic = computed(() => {
            return _.get(usePage().props.value, 'auth.user.partner_code', [])
        })

        const northPlaid = reactive({
            show: false,
            src: '',
            target: ''
        })

        const investingType = computed(() => {
            return store.state.profileForm.investingType
        })

        const form = useForm(_.merge({
            amount: '$0.00',
            capital_call_today: '$50,000.00',
            true_up_dollar: '$0',
            waive_fee: false,
            fund: {
                which: 'ach',
                accountName: '',
                nickname: '',
                accountType: '',
                accountNumber: '',
                routingNumber: '',
                plaid: false
            },
            fee: {
                which: 'no',
                accountName: '',
                nickname: '',
                accountType: 'Checking',
                accountNumber: '',
                routingNumber: '',
                plaid: false
            },
            distribution: {
                which: 'no',
                accountName: '',
                nickname: '',
                accountType: '',
                accountNumber: '',
                routingNumber: '',
                plaid: false
            },
        }, store.state.investForm));

        const amountError = ref('')

        /* const showFeeMessage = ref(true) */

        const showCheckReference = ref(false)

        /* const partnerFeeMessageCodes = ['capsouth', 'mattmcgirt', 'mammoth', 'coastal1']

        const partnerAdminFeeCodes = ['vestia', 'mammoth', 'coastal1']

        const selectedPartnerCode = _.toLower(_.get(store.state.suitabilityForm, 'partnerCode')) */

        const showFeeMessage = computed(() => {
            return !(partnerCodeLogic.value).includes('waive_late_entry_fee')
        })

        const waiveMinimumValue = computed(() => {
            return (partnerCodeLogic.value).includes('wavive_minimum_amount')
        })

        /* if (partnerFeeMessageCodes.includes(selectedPartnerCode)) {
            showFeeMessage.value = false
        } */

        /* if (partnerAdminFeeCodes.includes(selectedPartnerCode)) {
            form.waive_fee = true
        } else {
            form.waive_fee = false
        } */

        if (!_.isString(form.amount)) {
            form.amount = '$0.00'
        }

        if (!_.isString(form.capital_call_today)) {
            form.capital_call_today = '$50,000.00'
        }

        if (!_.isString(form.true_up_dollar)) {
            form.true_up_dollar = '$0.00'
        }

        if (moment().isSameOrAfter('2021-10-01') && moment().isSameOrBefore('2022-03-31')) {
            form.true_up_dollar = '$1,500.00'
        }

        if (moment().isSameOrAfter('2022-04-01')) {
            form.true_up_dollar = '$2,500.00'
        }

        onMounted(() => {
            Inertia.reload({
                only: ['auth'],
                onFinish: () => {
                    form.waive_fee = (partnerCodeLogic.value).includes('waive_admin_fee')
                    isReloaded.value = true
                }
            })

            window.addEventListener('message', postMessage);

            window.scroll(0, 0)

            if (investingType.value == 'ira') {
                form.fund.which = 'ira'
            }

            if (!['wire', 'ach', 'ira'].includes(form.fund.which)) {
                form.fund.which = 'ach'
            }

            if (form.fund.which == 'ach' && form.fee.which == 'yes') {
                copyFundToAccount('fund', 'fee')
            }

            if (form.distribution.which == 'yes') {
                copyFundToAccount('fee', 'distribution')
            }
        })

        onUnmounted(() => {
            window.removeEventListener('message', postMessage);
        })

        watch(() => form.capital_call_today, (value) => {
            const amount = value.replace(/[^0-9.]/g, '')

            console.log(amount)

            if (amount > 100000) {
                form.fund.which = 'wire'
            }
        })

        watch(() => form.fund, (value) => {
            if (value.which == 'ach' && form.fee.which == 'yes') {
                copyFundToAccount('fund', 'fee')
            }
        }, {deep: true})

        watch(() => form.fee, (value) => {
            if (form.distribution.which == 'yes') {
                copyFundToAccount('fee', 'distribution')
            }

            if (form.fund.which == 'ach' && value.which == 'yes') {
                copyFundToAccount('fund', 'fee')
            }
        }, {deep: true})

        watch(() => form.distribution.which, (value) => {
            if (value == 'yes') {
                copyFundToAccount('fee', 'distribution')
            }
        }, {deep: true})

        watch(() => northPlaid.show, (state) => {
            if (state) {
                document.body.classList.add('overflow-hidden')
            } else {
                document.body.classList.remove('overflow-hidden')
            }
        }, {
            immediate: true
        })

        const isCapitalCallHundredK = computed(() => {
            const value = form.capital_call_today.replace(/[^0-9.]/g, '')

            return (value > 100000) ? true : false
        })

        const showOtherAccount = computed(() => {
            if (form.fund.which != 'ach') {
                return true
            }

            if (form.fund.plaid) {
                return true
            }

            return false
        })

        function postMessage(event){
            var eventData = event.data || event.message

            eventData = (_.isObject(eventData)) ? eventData : JSON.parse(eventData)

            if (_.has(eventData, 'statusCode') || _.has(eventData, 'errorCode')) {
                northPlaid.show = false
                northPlaid.src = ''
                isBusy.value = false

                if (_.get(eventData, 'statusCode') == '101') {
                    form[northPlaid.target].plaid = true

                    var plaidAccountDetails = _.get(eventData, 'plaidAccountDetails.0')

                    form.fund.nickname = _.get(plaidAccountDetails, 'account_name') + ' - ' + _.get(plaidAccountDetails, 'institution_name')
                    form.fund.accountType = _.get(plaidAccountDetails, 'account_type')
                }

                if (_.has(eventData, 'errorCode')) {
                    isBusy.value = false
                }
            }
        }

        function copyFundToAccount(source, target, isReset = false){
            const keys = _.keys(_.omit(form[source], ['which', 'plaid']))

            _.each(keys, (key) => {
                form[target][key] = (!isReset) ? form[source][key] : ''
            })
        }

        function isAccountDisabled(account){
            if (account == 'fee') {
                return (form.fund.which == 'ach' && form[account].which == 'yes') ? true : false
            }

            return (form[account].which == 'yes') ? true : false
        }

        function viewInstructions(){
            window.scrollTo(0,0);
            form.reset();
        }

        function onAmountInput(e){
            const value = e.target.value.replace(/[^0-9.]/g, '')

            amountError.value = ''
            form.capital_call_today = value

            var minimumValue     = (props.series != 'xnsight') ? 25000 : 50000,
                minimumText      = (props.series != 'xnsight') ? '$25,000.00' : '$50,000.00',
                minimumShortText = (props.series != 'xnsight') ? '$25k' : '$50k'

            if (value < minimumValue && !waiveMinimumValue.value) {
                amountError.value = 'minimum amount of ' + minimumShortText
                form.capital_call_today = minimumText
            }

            if (value > 250000) {
                form.capital_call_today = '$250,000.00'
            }
        }

        async function handlePlaid(evt, target){
            isBusy.value = true

            var isUpdate = (target != 'fund' && form.fund.plaid) ? true : false

            await axios.post(route('intake.integrate.plaid'), {
                update: isUpdate
            }).then(response => {
                if (response.status == 200) {
                    northPlaid.src = response.data
                    northPlaid.show = true
                    northPlaid.target = target
                }
            }).catch(error => {
                console.log(error.response)
                alert('Ops! something went wrong.')
                isBusy.value = false
                northPlaid.show = false
            })
        }

        function handleBack(){
            emit('prev', true)
        }

        function handleNext(){
            const amount = form.amount.replace(/[^0-9.]/g, '')

            var minimumValue     = (props.series != 'xnsight') ? 25000 : 50000,
                minimumText      = (props.series != 'xnsight') ? '$25,000.00' : '$50,000.00',
                minimumShortText = (props.series != 'xnsight') ? '$25k' : '$50k'

            if (amount < minimumValue && !waiveMinimumValue.value) {
                alert('Minimum investment amount is ' + minimumShortText)
                return false
            }

            form.post(route('intake.validate.invest')).then(response => {
                store.commit('UPDATE_FORM_DATA', {
                    form: 'investForm',
                    data: form.data()
                });

                emit('next', true)
            }).catch(errors => {
                window.scroll(0,0)
                console.log(errors.response)
            })
        }

        return {
            investingType,
            northPlaid,
            isBusy,
            isReloaded,
            form,
            amountError,
            showFeeMessage,
            showCheckReference,
            isCapitalCallHundredK,
            showOtherAccount,
            isAccountDisabled,
            viewInstructions,
            onAmountInput,
            handlePlaid,
            handleBack,
            handleNext,
        }
    },
    computed: {
        paymentMethods(){
            return ['ACH Transfer', 'Wire']
        },
        accountTypes(){
            return (this.series == 'nsight') ? ['Checking', 'Savings', 'Brokerage'] : ['Checking', 'Savings', 'Brokerage', 'IRA/Roth IRA']
        },
        accountTypesAlt(){
            return ['Checking', 'Savings', 'Brokerage']
        },
        moneyArgs(){
            return {
                decimal: '.',
                thousands: ',',
                prefix: '$',
                precision: 2,
                masked: false
            }
        }
    }
}
</script>

<style>

</style>
